import { IPrintOrder, SpoolerStatus } from 'components/layoutsPrinter/IPrintOrder';
import {create} from 'zustand';

export type PrinterStore = {
  ordersToPrint: IPrintOrder[];
  spoolerStatus: SpoolerStatus;
  showPrintError: boolean;
  errorMessage: string;
  failedOrder: IPrintOrder | null;
  retryCount: number;
  addOrders: (printOrders: IPrintOrder[]) => void;
  removeOrder: (printOrderId: string) => void;
  setSpoolerStatus: (status: SpoolerStatus) => void;
  getOrderToPrint: () => IPrintOrder;
  tryStartPrinting: () => boolean;
  setPrintError: (show: boolean, message?: string, order?: IPrintOrder) => void;
  retryFailedPrint: () => void;
  skipFailedPrint: () => void;
};

export const usePrinterStore = create<PrinterStore>((set, get) => ({
  ordersToPrint: [],
  spoolerStatus: SpoolerStatus.standby,
  showPrintError: false,
  errorMessage: '',
  failedOrder: null,
  retryCount: 0,
  setSpoolerStatus: (status: SpoolerStatus) => set({spoolerStatus: status}),
  addOrders: printOrders => {
    set((state: PrinterStore) => {
      const ordersToPrint = [...state.ordersToPrint, ...printOrders];
      return {
        ordersToPrint,
      }
    });
  },
  getOrderToPrint: () => {
    const { ordersToPrint } = get();

    if (ordersToPrint.length === 0) {
      throw new Error('Nenhum pedido para ser impresso.');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [firstOrder, ..._remainingOrders] = ordersToPrint;

    return firstOrder;
  },
  removeOrder: (printOrderId: string) =>
    set((state: PrinterStore) => ({
      ordersToPrint: state.ordersToPrint.filter(order => order.id !== printOrderId),
    })),
  // Função atômica para tentar iniciar impressão
  // Retorna true apenas se conseguir mudar o status de standby para isPrinting
  // Isso garante que apenas uma impressão seja iniciada por vez
  tryStartPrinting: () => {
    const state = get();
    if (state.spoolerStatus === SpoolerStatus.standby && state.ordersToPrint.length > 0) {
      set({ spoolerStatus: SpoolerStatus.isPrinting });
      return true;
    }
    return false;
  },
  setPrintError: (show: boolean, message?: string, order?: IPrintOrder) => {
    set({ 
      showPrintError: show, 
      errorMessage: message || '', 
      failedOrder: order || null,
      spoolerStatus: show ? SpoolerStatus.standby : get().spoolerStatus
    });
  },
  retryFailedPrint: () => {
    const state = get();
    set({ 
      showPrintError: false, 
      errorMessage: '', 
      retryCount: state.retryCount + 1,
      spoolerStatus: SpoolerStatus.standby 
    });
  },
  skipFailedPrint: () => {
    const state = get();
    if (state.failedOrder) {
      set((currentState: PrinterStore) => ({
        ordersToPrint: currentState.ordersToPrint.filter(order => order.id !== state.failedOrder?.id),
        showPrintError: false,
        errorMessage: '',
        failedOrder: null,
        retryCount: 0,
        spoolerStatus: SpoolerStatus.standby
      }));
    }
  },
}));
